import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, X, Copy, Mail, UserMinus, Trash2 } from "lucide-react";
import { useLang } from "../LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAccessRequests() {
  const { t } = useLang();
  const [requests, setRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [links, setLinks] = useState({}); // id -> {url, sent}
  const [copied, setCopied] = useState("");

  const load = async () => {
    try {
      const [r, m] = await Promise.all([
        axios.get(`${API}/access/requests`, { withCredentials: true }),
        axios.get(`${API}/members`, { withCredentials: true }),
      ]);
      setRequests(r.data || []);
      setMembers(m.data || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    const { data } = await axios.post(`${API}/access/requests/${id}/approve`, {}, { withCredentials: true });
    setLinks((p) => ({ ...p, [id]: { url: data.link, sent: data.email_sent } }));
    load();
  };
  const reject = async (id) => {
    await axios.post(`${API}/access/requests/${id}/reject`, {}, { withCredentials: true });
    load();
  };
  const sendReset = async (uid) => {
    const { data } = await axios.post(`${API}/members/${uid}/send-reset`, {}, { withCredentials: true });
    setLinks((p) => ({ ...p, [uid]: { url: data.link, sent: data.email_sent } }));
  };
  const deactivate = async (uid) => {
    await axios.post(`${API}/members/${uid}/deactivate`, {}, { withCredentials: true });
    load();
  };
  const removeMember = async (uid) => {
    if (!window.confirm(t("memberx.confirm_delete"))) return;
    await axios.delete(`${API}/members/${uid}`, { withCredentials: true });
    load();
  };
  const removeRequest = async (id) => {
    if (!window.confirm(t("memberx.confirm_delete"))) return;
    await axios.delete(`${API}/access/requests/${id}`, { withCredentials: true });
    setLinks((p) => {
      const { [id]: _drop, ...rest } = p;
      return rest;
    });
    load();
  };
  const copy = (id, url) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(""), 2000);
  };

  const statusLabel = (s) => t(`memberx.${s}`);
  // Keep approved rows that still have an unrevealed link so admin can copy it (email disabled fallback)
  const pending = requests.filter((r) => r.status === "pending" || links[r.id]);

  const LinkRow = ({ id }) =>
    links[id] ? (
      <div className="mt-2 text-xs bg-cream-100 border border-cream-300 rounded-xl p-2.5 break-all" data-testid={`link-row-${id}`}>
        {!links[id].sent && <p className="text-amber-700 mb-1">{t("memberx.email_off_note")}</p>}
        <div className="flex items-center gap-2">
          <span className="flex-1 text-ink-muted">{links[id].url}</span>
          <button onClick={() => copy(id, links[id].url)} className="inline-flex items-center gap-1 text-forest hover:underline shrink-0" data-testid={`copy-${id}`}>
            <Copy size={13} /> {copied === id ? t("memberx.link_copied") : t("memberx.copy_link")}
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="mt-14" data-testid="admin-access-requests">
      <h2 className="font-heading text-2xl font-semibold text-ink mb-4">{t("memberx.admin_requests_title")}</h2>
      {pending.length === 0 ? (
        <p className="text-ink-muted text-sm">{t("memberx.admin_no_requests")}</p>
      ) : (
        <div className="space-y-3">
          {pending.map((r) => (
            <div key={r.id} className="card-soft p-5" data-testid={`request-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="text-sm">
                  <div className="font-semibold text-ink">{r.full_name} <span className="text-ink-muted font-normal">· {t(`memberx.role_${r.role}`)}</span></div>
                  <div className="text-ink-muted">{r.email}</div>
                  {r.family_name && <div className="text-ink-muted">{t("memberx.f_family")}: {r.family_name}</div>}
                  {r.child_name && <div className="text-ink-muted">{t("memberx.f_child")}: {r.child_name}</div>}
                  {r.parent_email && <div className="text-ink-muted">{t("memberx.f_parent_email")}: {r.parent_email}</div>}
                  {r.message && <div className="text-ink-muted italic mt-1">“{r.message}”</div>}
                </div>
                <div className="flex gap-2">
                  {links[r.id] ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-4 py-2 bg-forest-light text-forest text-sm" data-testid={`approved-${r.id}`}>
                      <Check size={15} /> {t("memberx.approved")}
                    </span>
                  ) : (
                    <>
                      <button onClick={() => approve(r.id)} className="inline-flex items-center gap-1 rounded-full px-4 py-2 bg-forest text-cream-50 text-sm hover:bg-forest-dark" data-testid={`approve-${r.id}`}>
                        <Check size={15} /> {t("memberx.approve")}
                      </button>
                      <button onClick={() => reject(r.id)} className="inline-flex items-center gap-1 rounded-full px-4 py-2 border border-cream-300 text-ink-muted text-sm hover:border-red-300 hover:text-red-700" data-testid={`reject-${r.id}`}>
                        <X size={15} /> {t("memberx.reject")}
                      </button>
                    </>
                  )}
                  <button onClick={() => removeRequest(r.id)} title={t("memberx.delete")} className="inline-flex items-center gap-1 rounded-full px-3.5 py-2 border border-cream-300 text-ink-muted text-sm hover:border-red-300 hover:text-red-700" data-testid={`delete-request-${r.id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <LinkRow id={r.id} />
            </div>
          ))}
        </div>
      )}

      <h2 className="font-heading text-2xl font-semibold text-ink mt-10 mb-4">{t("memberx.admin_members_title")}</h2>
      <div className="space-y-2">
        {members.map((m) => (
          <div key={m.id} className="card-soft p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`member-${m.id}`}>
            <div className="text-sm">
              <span className="font-semibold text-ink">{m.name}</span>
              <span className="text-ink-muted"> · {m.email}</span>
              {m.active === false && <span className="ml-2 text-xs text-amber-700">(inactif)</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => sendReset(m.id)} className="inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 border border-cream-300 text-forest text-xs hover:bg-forest hover:text-cream-50" data-testid={`reset-${m.id}`}>
                <Mail size={13} /> {t("memberx.send_reset")}
              </button>
              <button onClick={() => deactivate(m.id)} className="inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 border border-cream-300 text-ink-muted text-xs hover:border-red-300 hover:text-red-700" data-testid={`deactivate-${m.id}`}>
                <UserMinus size={13} /> {t("memberx.deactivate")}
              </button>
              <button onClick={() => removeMember(m.id)} className="inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 border border-cream-300 text-ink-muted text-xs hover:border-red-300 hover:text-red-700" data-testid={`delete-member-${m.id}`}>
                <Trash2 size={13} /> {t("memberx.delete")}
              </button>
            </div>
            <LinkRow id={m.id} />
          </div>
        ))}
      </div>
    </div>
  );
}
